import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Briefcase, TrendingUp, MoreVertical, Trash2, Edit2, Loader2, LogOut, Shield, ChevronDown, Key, Database, Search, X, Download, Sparkles, User, ClipboardCheck, RefreshCw, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LanguageSelector from "@/components/LanguageSelector";

const BOOK_AD = {
  imageDe: "https://customer-assets.emergentagent.com/job_16b7dcc6-2587-4e21-8a51-3bf4baf0cc8d/artifacts/zh6g0ivn_Kindle-Cover-Front-Thumb-De.jpg",
  imageEn: "https://customer-assets.emergentagent.com/job_16b7dcc6-2587-4e21-8a51-3bf4baf0cc8d/artifacts/3numn9l2_Kindle-Cover-Front-Thumb-En.jpg",
  imageFr: "https://customer-assets.emergentagent.com/job_16b7dcc6-2587-4e21-8a51-3bf4baf0cc8d/artifacts/1ke0de9g_Kindle-Cover-Front-Thumb-fr.jpg",
  linkDe: "http://www.amazon.de/exec/obidos/ASIN/3033122248/buchempfehlumark",
  linkEn: "http://www.amazon.de/exec/obidos/ASIN/3033122930/buchempfehlumark",
  linkFr: "https://www.amazon.fr/dp/3033123260",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  
  // Change password state
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);

  // Asset Lookup state
  const [assetLookupOpen, setAssetLookupOpen] = useState(false);
  const [assetLookups, setAssetLookups] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [assetClassFilter, setAssetClassFilter] = useState("");
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetFormOpen, setAssetFormOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetFormData, setAssetFormData] = useState({ 
    symbol: "", name: "", currency: "", 
    asset_class_en: "", asset_class_de: "", asset_class_fr: "",
    sector_en: "", sector_de: "", sector_fr: "",
    factor_en: "", factor_de: "", factor_fr: "",
    geography_en: "", geography_de: "", geography_fr: ""
  });
  const [assetSubmitting, setAssetSubmitting] = useState(false);
  const [lookupLang, setLookupLang] = useState(i18n.language || 'en');
  
  // Yahoo import state - search-based
  const [yahooSearchQuery, setYahooSearchQuery] = useState("");
  const [yahooNameSearchQuery, setYahooNameSearchQuery] = useState("");
  const [yahooSearchResults, setYahooSearchResults] = useState([]);
  const [yahooSearching, setYahooSearching] = useState(false);
  const [yahooSelectedAsset, setYahooSelectedAsset] = useState(null);
  const [yahooAssetClassEn, setYahooAssetClassEn] = useState("");
  const [yahooAssetClassDe, setYahooAssetClassDe] = useState("");
  const [yahooAssetClassFr, setYahooAssetClassFr] = useState("");
  const [yahooSectorEn, setYahooSectorEn] = useState("");
  const [yahooSectorDe, setYahooSectorDe] = useState("");
  const [yahooSectorFr, setYahooSectorFr] = useState("");
  const [yahooFactorEn, setYahooFactorEn] = useState("");
  const [yahooFactorDe, setYahooFactorDe] = useState("");
  const [yahooFactorFr, setYahooFactorFr] = useState("");
  const [yahooGeographyEn, setYahooGeographyEn] = useState("");
  const [yahooGeographyDe, setYahooGeographyDe] = useState("");
  const [yahooGeographyFr, setYahooGeographyFr] = useState("");
  const [yahooImporting, setYahooImporting] = useState(false);
  const [activeTab, setActiveTab] = useState("list");

  // AI Analysis state
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analyzingPortfolio, setAnalyzingPortfolio] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Risk Profile state
  const [riskProfile, setRiskProfile] = useState(null);
  const [riskAttitudeOpen, setRiskAttitudeOpen] = useState(false);
  const [riskCapacityOpen, setRiskCapacityOpen] = useState(false);
  const [riskAttitudeAnswers, setRiskAttitudeAnswers] = useState({ q1: null, q2: null, q3: null, q4: null });
  const [riskCapacityAnswers, setRiskCapacityAnswers] = useState({ q5: null, q6: null, q7: null, q8: null });
  const [savingRiskProfile, setSavingRiskProfile] = useState(false);

  useEffect(() => {
    fetchPortfolios();
    fetchRiskProfile();
  }, []);

  const fetchPortfolios = async () => {
    try {
      const data = await api.getPortfolios();
      setPortfolios(data);
    } catch (error) {
      toast.error("Failed to load portfolios");
    } finally {
      setLoading(false);
    }
  };

  const fetchRiskProfile = async () => {
    try {
      const profile = await api.getRiskProfile();
      setRiskProfile(profile);
      // Set answers from profile
      if (profile) {
        setRiskAttitudeAnswers({
          q1: profile.risk_attitude_q1,
          q2: profile.risk_attitude_q2,
          q3: profile.risk_attitude_q3,
          q4: profile.risk_attitude_q4
        });
        setRiskCapacityAnswers({
          q5: profile.risk_capacity_q5,
          q6: profile.risk_capacity_q6,
          q7: profile.risk_capacity_q7,
          q8: profile.risk_capacity_q8
        });
      }
    } catch (error) {
      console.error("Failed to load risk profile:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t('auth.loggedOut'));
    navigate("/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error(t('auth.fillAllFields'));
      return;
    }
    
    if (passwordData.new !== passwordData.confirm) {
      toast.error(t('auth.passwordsNoMatch'));
      return;
    }
    
    if (passwordData.new.length < 6) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.changePassword(passwordData.current, passwordData.new);
      toast.success(t('password.changed'));
      setPasswordOpen(false);
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('password.changeFailed'));
    } finally {
      setChangingPassword(false);
    }
  };

  // ==================== ASSET LOOKUP FUNCTIONS ====================

  const fetchAssetLookups = async (search = assetSearchQuery, assetClass = assetClassFilter) => {
    setAssetLoading(true);
    try {
      const [assets, classes] = await Promise.all([
        api.getAssetLookups(search || null, assetClass || null),
        api.getAssetClasses()
      ]);
      setAssetLookups(assets);
      setAssetClasses(classes);
    } catch (error) {
      toast.error(t('errors.failedLoadData'));
    } finally {
      setAssetLoading(false);
    }
  };

  const handleAssetLookupOpen = () => {
    setAssetLookupOpen(true);
    fetchAssetLookups("", "");
  };

  const handleAssetSearch = () => {
    fetchAssetLookups(assetSearchQuery, assetClassFilter);
  };

  const handleAssetClassChange = (value) => {
    setAssetClassFilter(value === "all" ? "" : value);
    fetchAssetLookups(assetSearchQuery, value === "all" ? "" : value);
  };

  const openAssetForm = (asset = null) => {
    if (asset) {
      setEditingAsset(asset);
      setAssetFormData({
        symbol: asset.symbol,
        name: asset.name,
        currency: asset.currency,
        asset_class_en: asset.asset_class_en || "",
        asset_class_de: asset.asset_class_de || "",
        asset_class_fr: asset.asset_class_fr || "",
        sector_en: asset.sector_en || "",
        sector_de: asset.sector_de || "",
        sector_fr: asset.sector_fr || "",
        factor_en: asset.factor_en || "",
        factor_de: asset.factor_de || "",
        factor_fr: asset.factor_fr || "",
        geography_en: asset.geography_en || "",
        geography_de: asset.geography_de || "",
        geography_fr: asset.geography_fr || ""
      });
    } else {
      setEditingAsset(null);
      setAssetFormData({ 
        symbol: "", name: "", currency: "", 
        asset_class_en: "", asset_class_de: "", asset_class_fr: "",
        sector_en: "", sector_de: "", sector_fr: "",
        factor_en: "", factor_de: "", factor_fr: "",
        geography_en: "", geography_de: "", geography_fr: ""
      });
    }
    setAssetFormOpen(true);
  };

  const handleAssetFormSubmit = async (e) => {
    e.preventDefault();
    if (!assetFormData.symbol.trim()) {
      toast.error(t('assetLookup.symbolRequired'));
      return;
    }
    if (!assetFormData.name.trim()) {
      toast.error(t('assetLookup.nameRequired'));
      return;
    }
    if (!assetFormData.currency.trim()) {
      toast.error(t('assetLookup.currencyRequired'));
      return;
    }
    if (!assetFormData.asset_class_en.trim()) {
      toast.error(t('assetLookup.assetClassEnRequired'));
      return;
    }
    if (!assetFormData.asset_class_de.trim()) {
      toast.error(t('assetLookup.assetClassDeRequired'));
      return;
    }

    setAssetSubmitting(true);
    try {
      if (editingAsset) {
        await api.updateAssetLookup(editingAsset.id, assetFormData);
        toast.success(t('assetLookup.assetUpdated'));
      } else {
        await api.createAssetLookup(assetFormData);
        toast.success(t('assetLookup.assetCreated'));
      }
      setAssetFormOpen(false);
      setEditingAsset(null);
      setAssetFormData({ 
        symbol: "", name: "", currency: "", 
        asset_class_en: "", asset_class_de: "", asset_class_fr: "",
        sector_en: "", sector_de: "", sector_fr: "",
        factor_en: "", factor_de: "", factor_fr: "",
        geography_en: "", geography_de: "", geography_fr: ""
      });
      fetchAssetLookups();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.error(t('assetLookup.symbolExists'));
      } else {
        toast.error(error.response?.data?.detail || t('errors.failedCreatePortfolio'));
      }
    } finally {
      setAssetSubmitting(false);
    }
  };

  const handleAssetDelete = async (assetId) => {
    if (!window.confirm(t('assetLookup.confirmDelete'))) return;
    
    try {
      await api.deleteAssetLookup(assetId);
      toast.success(t('assetLookup.assetDeleted'));
      fetchAssetLookups();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleDownloadCSV = () => {
    if (!assetLookups.length) return;
    const headers = [
      'Symbol', 'Name', 'Currency',
      'Asset Class (EN)', 'Asset Class (DE)', 'Asset Class (FR)',
      'Sector (EN)', 'Sector (DE)', 'Sector (FR)',
      'Factor (EN)', 'Factor (DE)', 'Factor (FR)',
      'Geography (EN)', 'Geography (DE)', 'Geography (FR)'
    ];
    const escape = (v) => {
      const s = String(v || '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = assetLookups.map(a => [
      a.symbol, a.name, a.currency,
      a.asset_class_en, a.asset_class_de, a.asset_class_fr,
      a.sector_en, a.sector_de, a.sector_fr,
      a.factor_en, a.factor_de, a.factor_fr,
      a.geography_en, a.geography_de, a.geography_fr
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'asset_lookup_table.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Yahoo search function
  const handleYahooSearch = async () => {
    if (!yahooSearchQuery.trim() && !yahooNameSearchQuery.trim()) {
      return;
    }
    setYahooSearching(true);
    setYahooSearchResults([]);
    try {
      const results = await api.searchSymbols(yahooSearchQuery || null, yahooNameSearchQuery || null);
      setYahooSearchResults(results);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setYahooSearching(false);
    }
  };

  const handleYahooImport = async (e) => {
    e.preventDefault();
    if (!yahooSelectedAsset) {
      toast.error(t('assetLookup.symbolRequired'));
      return;
    }
    if (!yahooAssetClassEn.trim()) {
      toast.error(t('assetLookup.assetClassEnRequired'));
      return;
    }
    if (!yahooAssetClassDe.trim()) {
      toast.error(t('assetLookup.assetClassDeRequired'));
      return;
    }

    setYahooImporting(true);
    try {
      await api.importAssetFromYahoo(
        yahooSelectedAsset.symbol, 
        yahooAssetClassEn, yahooAssetClassDe, yahooAssetClassFr,
        yahooSectorEn, yahooSectorDe, yahooSectorFr,
        yahooFactorEn, yahooFactorDe, yahooFactorFr,
        yahooGeographyEn, yahooGeographyDe, yahooGeographyFr
      );
      toast.success(t('assetLookup.assetImported'));
      // Reset form
      resetYahooImportForm();
      // Switch back to list tab
      setActiveTab("list");
      fetchAssetLookups();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.error(t('assetLookup.symbolExists'));
      } else {
        toast.error(error.response?.data?.detail || t('errors.generic'));
      }
    } finally {
      setYahooImporting(false);
    }
  };

  const resetYahooImportForm = () => {
    setYahooSearchQuery("");
    setYahooNameSearchQuery("");
    setYahooSearchResults([]);
    setYahooSelectedAsset(null);
    setYahooAssetClassEn("");
    setYahooAssetClassDe("");
    setYahooAssetClassFr("");
    setYahooSectorEn("");
    setYahooSectorDe("");
    setYahooSectorFr("");
    setYahooFactorEn("");
    setYahooFactorDe("");
    setYahooFactorFr("");
    setYahooGeographyEn("");
    setYahooGeographyDe("");
    setYahooGeographyFr("");
  };

  // ==================== PORTFOLIO FUNCTIONS ====================

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(t('dashboard.nameRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await api.createPortfolio(formData);
      toast.success(t('dashboard.portfolioCreated'));
      setCreateOpen(false);
      setFormData({ name: "", description: "" });
      fetchPortfolios();
    } catch (error) {
      toast.error(t('errors.failedCreatePortfolio'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(t('dashboard.nameRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await api.updatePortfolio(editingPortfolio.id, formData);
      toast.success(t('dashboard.portfolioUpdated'));
      setEditOpen(false);
      setEditingPortfolio(null);
      setFormData({ name: "", description: "" });
      fetchPortfolios();
    } catch (error) {
      toast.error(t('errors.failedUpdatePortfolio'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deletePortfolio(id);
      toast.success(t('dashboard.portfolioDeleted'));
      fetchPortfolios();
    } catch (error) {
      toast.error(t('errors.failedDeletePortfolio'));
    }
  };

  const openEditDialog = (portfolio) => {
    setEditingPortfolio(portfolio);
    setFormData({ name: portfolio.name, description: portfolio.description || "" });
    setEditOpen(true);
  };

  // AI Portfolio Analysis
  const handleAnalyzePortfolio = async (portfolio, e) => {
    e.stopPropagation();
    
    if (!portfolio.assets || portfolio.assets.length === 0) {
      toast.error(t('analysis.noAssets'));
      return;
    }
    
    setAnalyzingPortfolio(portfolio);
    setAnalysisResult(null);
    setAnalysisOpen(true);
    setAnalyzing(true);
    
    try {
      const result = await api.analyzePortfolio(portfolio.id, i18n.language);
      setAnalysisResult(result);
    } catch (error) {
      toast.error(t('analysis.failed'));
      console.error('Analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  // Risk Profile handlers
  const handleSaveRiskAttitude = async () => {
    setSavingRiskProfile(true);
    try {
      const profile = await api.saveRiskAttitude({
        risk_attitude_q1: riskAttitudeAnswers.q1,
        risk_attitude_q2: riskAttitudeAnswers.q2,
        risk_attitude_q3: riskAttitudeAnswers.q3,
        risk_attitude_q4: riskAttitudeAnswers.q4
      });
      setRiskProfile(profile);
      setRiskAttitudeOpen(false);
      toast.success(t('riskProfile.saved'));
    } catch (error) {
      toast.error('Failed to save risk profile');
    } finally {
      setSavingRiskProfile(false);
    }
  };

  const handleSaveRiskCapacity = async () => {
    setSavingRiskProfile(true);
    try {
      const profile = await api.saveRiskCapacity({
        risk_capacity_q5: riskCapacityAnswers.q5,
        risk_capacity_q6: riskCapacityAnswers.q6,
        risk_capacity_q7: riskCapacityAnswers.q7,
        risk_capacity_q8: riskCapacityAnswers.q8
      });
      setRiskProfile(profile);
      setRiskCapacityOpen(false);
      toast.success(t('riskProfile.saved'));
    } catch (error) {
      toast.error('Failed to save risk profile');
    } finally {
      setSavingRiskProfile(false);
    }
  };

  const getRiskAttitudeLabel = (avg) => {
    if (avg === null || avg === undefined) return t('riskProfile.notCompleted');
    if (avg >= 3.25) return t('riskProfile.attitudeHigh');
    if (avg >= 2.25) return t('riskProfile.attitudeMedium');
    return t('riskProfile.attitudeLow');
  };

  const getRiskCapacityLabel = (avg) => {
    if (avg === null || avg === undefined) return t('riskProfile.notCompleted');
    if (avg >= 3.25) return t('riskProfile.capacityHigh');
    if (avg >= 2.25) return t('riskProfile.capacityMedium');
    return t('riskProfile.capacityLow');
  };

  const getRiskColor = (avg) => {
    if (avg === null || avg === undefined) return 'text-slate-400';
    if (avg >= 3.25) return 'text-green-600';
    if (avg >= 2.25) return 'text-amber-600';
    return 'text-red-600';
  };

  const totalAssets = portfolios.reduce((acc, p) => acc + (p.assets?.length || 0), 0);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 truncate" style={{ fontFamily: 'Manrope' }}>
                {t('dashboard.title')}
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm mt-0.5 sm:mt-1 hidden sm:block">{t('dashboard.subtitle')}</p>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
              {/* Language Selector - moved to header */}
              <LanguageSelector />
              
              {/* Asset Lookup Button - Admin Only */}
              {user?.is_admin && (
                <Button 
                  variant="outline"
                  data-testid="asset-lookup-btn"
                  onClick={handleAssetLookupOpen}
                  className="border-slate-200 h-8 sm:h-10 px-2 sm:px-4 text-xs sm:text-sm"
                >
                  <Database className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{t('assetLookup.title')}</span>
                </Button>
              )}
              
              {/* Clear Price Cache Button - Admin Only */}
              {user?.is_admin && (
                <Button 
                  variant="outline"
                  data-testid="clear-cache-btn"
                  onClick={async () => {
                    if (!window.confirm(t('admin.confirmClearCache', 'Clear all cached price data? Prices will be re-fetched from Yahoo Finance on next access.'))) return;
                    try {
                      const result = await api.clearPriceCache();
                      toast.success(result.message);
                    } catch (e) {
                      toast.error('Failed to clear cache');
                    }
                  }}
                  className="border-slate-200 h-8 sm:h-10 px-2 sm:px-4 text-xs sm:text-sm"
                >
                  <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{t('admin.clearCache', 'Clear Cache')}</span>
                </Button>
              )}
              
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="create-portfolio-btn"
                    className="bg-[#0F766E] hover:bg-[#115E59] text-white rounded-full px-3 sm:px-6 h-8 sm:h-10 text-xs sm:text-sm"
                  >
                    <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" strokeWidth={1.5} />
                    <span className="hidden sm:inline">{t('dashboard.newPortfolio')}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope' }} className="text-base sm:text-lg">{t('dashboard.createPortfolio')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="name" className="text-sm">{t('dashboard.portfolioName')}</Label>
                      <Input
                        id="name"
                        data-testid="portfolio-name-input"
                        placeholder={t('dashboard.portfolioNamePlaceholder')}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="h-10 sm:h-12 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="description" className="text-sm">{t('dashboard.descriptionOptional')}</Label>
                      <Textarea
                        id="description"
                        data-testid="portfolio-description-input"
                        placeholder={t('dashboard.descriptionPlaceholder')}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                      <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} className="text-sm h-9 sm:h-10">
                        {t('common.cancel')}
                      </Button>
                      <Button 
                        type="submit" 
                        data-testid="submit-create-portfolio"
                        disabled={submitting}
                        className="bg-[#0F766E] hover:bg-[#115E59] text-sm h-9 sm:h-10"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.create')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-1.5 sm:gap-2 h-8 sm:h-10 px-2 sm:px-3" data-testid="user-menu-btn">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-teal-700 text-xs font-medium">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                    <span className="text-xs sm:text-sm font-medium hidden md:inline">{user?.name}</span>
                    {user?.is_admin && (
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] sm:text-xs hidden md:inline-flex">
                        {t('admin.admin')}
                      </Badge>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 sm:w-56">
                  <div className="px-2 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {user?.is_admin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-menu-item">
                        <Shield className="w-4 h-4 mr-2 text-amber-600" />
                        {t('userMenu.adminDashboard')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => setPasswordOpen(true)} data-testid="change-password-btn">
                    <Key className="w-4 h-4 mr-2" />
                    {t('userMenu.changePassword')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('userMenu.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 sm:py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-teal-50 rounded-lg sm:rounded-xl">
                  <Briefcase className="w-4 h-4 sm:w-6 sm:h-6 text-[#0F766E]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">{t('dashboard.totalPortfolios')}</p>
                  <p className="text-lg sm:text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                    {portfolios.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-teal-50 rounded-lg sm:rounded-xl">
                  <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-[#0F766E]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">{t('dashboard.totalAssets')}</p>
                  <p className="text-lg sm:text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                    {totalAssets}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Investor Profile Box */}
        <Card className="mb-4 sm:mb-6 border-slate-200 shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <CardHeader className="pb-2 p-3 sm:p-4">
            <CardTitle className="text-sm sm:text-base font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <User className="w-4 h-4 text-blue-600" />
              {t('riskProfile.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Buttons */}
              <div className="flex gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="risk-attitude-btn"
                  onClick={() => setRiskAttitudeOpen(true)}
                  className="text-xs h-8 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                >
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
                  {t('riskProfile.riskAttitude')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="risk-capacity-btn"
                  onClick={() => setRiskCapacityOpen(true)}
                  className="text-xs h-8 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                >
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
                  {t('riskProfile.riskCapacity')}
                </Button>
              </div>
              {/* Results */}
              <div className="flex gap-4 sm:gap-6 text-xs sm:text-sm">
                <div>
                  <span className="text-slate-500">{t('riskProfile.riskAttitude')}: </span>
                  <span className={`font-semibold ${getRiskColor(riskProfile?.risk_attitude_avg)}`}>
                    {getRiskAttitudeLabel(riskProfile?.risk_attitude_avg)}
                    {riskProfile?.risk_attitude_avg && ` (${riskProfile.risk_attitude_avg.toFixed(2)})`}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">{t('riskProfile.riskCapacity')}: </span>
                  <span className={`font-semibold ${getRiskColor(riskProfile?.risk_capacity_avg)}`}>
                    {getRiskCapacityLabel(riskProfile?.risk_capacity_avg)}
                    {riskProfile?.risk_capacity_avg && ` (${riskProfile.risk_capacity_avg.toFixed(2)})`}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Portfolio Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12 sm:py-20">
            <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-[#0F766E]" />
          </div>
        ) : portfolios.length === 0 ? (
          <div className="empty-state py-10 sm:py-16">
            <img 
              src="https://images.unsplash.com/photo-1676256632648-422a85b88b4c?w=300&h=200&fit=crop" 
              alt="Empty state"
              className="w-32 h-20 sm:w-48 sm:h-32 object-cover rounded-xl mb-4 sm:mb-6 opacity-80"
            />
            <h3 className="text-lg sm:text-xl font-semibold text-slate-700 mb-2" style={{ fontFamily: 'Manrope' }}>
              {t('dashboard.noPortfolios')}
            </h3>
            <p className="text-slate-500 mb-4 sm:mb-6 text-sm sm:text-base px-4">{t('dashboard.noPortfoliosText')}</p>
            <Button 
              onClick={() => setCreateOpen(true)}
              className="bg-[#0F766E] hover:bg-[#115E59] text-white rounded-full px-4 sm:px-6 text-sm"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {t('dashboard.createPortfolio')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            <AnimatePresence>
              {portfolios.map((portfolio, index) => (
                <motion.div
                  key={portfolio.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    data-testid={`portfolio-card-${portfolio.id}`}
                    className="portfolio-card cursor-pointer group"
                    onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                  >
                    <CardHeader className="flex flex-row items-start justify-between pb-1 p-3 sm:p-4">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-lg font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope' }}>
                          {portfolio.name}
                        </CardTitle>
                        {portfolio.description && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                            {portfolio.description}
                          </p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            data-testid={`edit-portfolio-${portfolio.id}`}
                            onClick={(e) => { e.stopPropagation(); openEditDialog(portfolio); }}
                          >
                            <Edit2 className="w-4 h-4 mr-2" /> {t('common.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            data-testid={`delete-portfolio-${portfolio.id}`}
                            className="text-red-600"
                            onClick={(e) => { e.stopPropagation(); handleDelete(portfolio.id); }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardHeader>
                    <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4">
                      <div className="flex items-center justify-between py-2 border-t border-slate-100">
                        <div>
                          <p className="text-[10px] sm:text-xs text-slate-500">{t('common.assets')}</p>
                          <p className="text-base sm:text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
                            {portfolio.assets?.length || 0}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] sm:text-xs text-slate-500">{t('dashboard.created')}</p>
                          <p className="text-[10px] sm:text-xs text-slate-700">
                            {new Date(portfolio.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {/* Open Portfolio Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`open-portfolio-${portfolio.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/portfolio/${portfolio.id}`); }}
                        className="w-full mt-2 text-xs h-8 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                      >
                        <Briefcase className="w-3.5 h-3.5 mr-1.5" />
                        {t('dashboard.openPortfolio')}
                      </Button>
                      {/* Analyze Portfolio Button */}
                      {portfolio.assets?.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`analyze-portfolio-${portfolio.id}`}
                          onClick={(e) => handleAnalyzePortfolio(portfolio, e)}
                          className="w-full mt-2 text-xs h-8 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 text-purple-700 hover:from-purple-100 hover:to-blue-100 hover:border-purple-300"
                        >
                          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                          {t('analysis.analyzePortfolio')}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Book Promotion */}
        <div className="mt-10 sm:mt-16" data-testid="book-ad">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-7 bg-gradient-to-br from-slate-50 to-teal-50/50 border border-slate-200 rounded-2xl p-5 sm:p-7 max-w-3xl mx-auto">
            <img
              src={i18n.language?.startsWith('de') ? BOOK_AD.imageDe : i18n.language?.startsWith('fr') ? BOOK_AD.imageFr : BOOK_AD.imageEn}
              alt={t('bookAd.title')}
              className="w-24 sm:w-32 h-auto rounded-lg shadow-md flex-shrink-0"
              data-testid="book-ad-cover"
            />
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                <span className="font-bold text-slate-900">{t('bookAd.title')}</span>: {t('bookAd.description')}
              </p>
              <a
                href={i18n.language?.startsWith('de') ? BOOK_AD.linkDe : i18n.language?.startsWith('fr') ? BOOK_AD.linkFr : BOOK_AD.linkEn}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-ad-button"
                className="inline-flex items-center gap-1.5 mt-4 bg-[#0F766E] hover:bg-[#115E59] text-white text-sm font-medium rounded-full px-5 py-2 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                {t('bookAd.button')}
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="text-base sm:text-lg">{t('dashboard.editPortfolio')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="edit-name" className="text-sm">{t('dashboard.portfolioName')}</Label>
              <Input
                id="edit-name"
                data-testid="edit-portfolio-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-10 sm:h-12 text-sm"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="edit-description" className="text-sm">{t('dashboard.description')}</Label>
              <Textarea
                id="edit-description"
                data-testid="edit-portfolio-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)} className="text-sm h-9 sm:h-10">
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                data-testid="submit-edit-portfolio"
                disabled={submitting}
                className="bg-[#0F766E] hover:bg-[#115E59] text-sm h-9 sm:h-10"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordOpen} onOpenChange={(open) => {
        setPasswordOpen(open);
        if (!open) setPasswordData({ current: "", new: "", confirm: "" });
      }}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2 text-base sm:text-lg">
              <Key className="w-4 h-4 sm:w-5 sm:h-5 text-[#0F766E]" />
              {t('password.change')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="current-password" className="text-sm">{t('password.current')}</Label>
              <Input
                id="current-password"
                type="password"
                data-testid="current-password-input"
                placeholder={t('password.enterCurrent')}
                value={passwordData.current}
                onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                className="h-10 sm:h-12 text-sm"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="new-password" className="text-sm">{t('password.new')}</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="new-password-input"
                placeholder={t('password.enterNew')}
                value={passwordData.new}
                onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                className="h-10 sm:h-12 text-sm"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="confirm-password" className="text-sm">{t('password.confirmNew')}</Label>
              <Input
                id="confirm-password"
                type="password"
                data-testid="confirm-password-input"
                placeholder={t('password.confirmNewPlaceholder')}
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                className="h-10 sm:h-12 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
              <Button type="button" variant="ghost" onClick={() => setPasswordOpen(false)} className="text-sm h-9 sm:h-10">
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                data-testid="submit-change-password"
                disabled={changingPassword}
                className="bg-[#0F766E] hover:bg-[#115E59] text-sm h-9 sm:h-10"
              >
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : t('password.change')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Asset Lookup Dialog - Admin Only */}
      <Dialog open={assetLookupOpen} onOpenChange={(open) => {
        setAssetLookupOpen(open);
        if (!open) {
          setAssetSearchQuery("");
          setAssetClassFilter("");
          setAssetFormOpen(false);
          setEditingAsset(null);
          setActiveTab("list");
          setLookupLang(i18n.language || 'en');
          resetYahooImportForm();
        }
      }}>
        <DialogContent className="sm:max-w-4xl mx-2 sm:mx-auto max-h-[90vh] overflow-hidden flex flex-col p-3 sm:p-6" aria-describedby={undefined}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2 text-base sm:text-lg">
                <Database className="w-4 h-4 sm:w-5 sm:h-5 text-[#0F766E]" />
                {t('assetLookup.title')}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setLookupLang('en')}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${lookupLang === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid="lookup-lang-en"
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLookupLang('de')}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${lookupLang === 'de' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid="lookup-lang-de"
                  >
                    DE
                  </button>
                  <button
                    onClick={() => setLookupLang('fr')}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${lookupLang === 'fr' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid="lookup-lang-fr"
                  >
                    FR
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadCSV}
                  disabled={!assetLookups.length}
                  className="h-8 px-2.5 text-xs gap-1.5"
                  data-testid="lookup-download-csv"
                >
                  <Download className="w-3.5 h-3.5" />
                  CSV
                </Button>
              </div>
            </div>
          </DialogHeader>

          {/* Add/Edit Asset Form */}
          {assetFormOpen ? (
            <div className="space-y-3 sm:space-y-4 mt-3 max-h-[60vh] overflow-y-auto">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm sm:text-base">
                  {editingAsset ? t('assetLookup.editAsset') : t('assetLookup.addAsset')}
                </h3>
                <Button variant="ghost" size="icon" onClick={() => {
                  setAssetFormOpen(false);
                  setEditingAsset(null);
                  setAssetFormData({ 
                    symbol: "", name: "", currency: "", 
                    asset_class_en: "", asset_class_de: "", asset_class_fr: "",
                    sector_en: "", sector_de: "", sector_fr: "",
                    factor_en: "", factor_de: "", factor_fr: "",
                    geography_en: "", geography_de: "", geography_fr: ""
                  });
                }} className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleAssetFormSubmit} className="space-y-3">
                {/* Symbol, Currency, Name */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.symbol')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterSymbol')}
                      value={assetFormData.symbol}
                      onChange={(e) => setAssetFormData({ ...assetFormData, symbol: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                      disabled={!!editingAsset}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.currency')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterCurrency')}
                      value={assetFormData.currency}
                      onChange={(e) => setAssetFormData({ ...assetFormData, currency: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">{t('assetLookup.name')}</Label>
                  <Input
                    placeholder={t('assetLookup.enterName')}
                    value={assetFormData.name}
                    onChange={(e) => setAssetFormData({ ...assetFormData, name: e.target.value })}
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                
                {/* Asset Class */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.assetClassEn')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterAssetClassEn')}
                      value={assetFormData.asset_class_en}
                      onChange={(e) => setAssetFormData({ ...assetFormData, asset_class_en: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                      list="asset-class-suggestions-en"
                    />
                    <datalist id="asset-class-suggestions-en">
                      {assetClasses.map((ac) => (
                        <option key={ac.en} value={ac.en} />
                      ))}
                    </datalist>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.assetClassDe')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterAssetClassDe')}
                      value={assetFormData.asset_class_de}
                      onChange={(e) => setAssetFormData({ ...assetFormData, asset_class_de: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                      list="asset-class-suggestions-de"
                    />
                    <datalist id="asset-class-suggestions-de">
                      {assetClasses.map((ac) => (
                        <option key={ac.de} value={ac.de} />
                      ))}
                    </datalist>
                  </div>
                </div>

                {/* Asset Class FR */}
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">{t('assetLookup.assetClassFr')}</Label>
                  <Input
                    placeholder={t('assetLookup.enterAssetClassFr')}
                    value={assetFormData.asset_class_fr}
                    onChange={(e) => setAssetFormData({ ...assetFormData, asset_class_fr: e.target.value })}
                    className="h-9 sm:h-10 text-sm"
                    list="asset-class-suggestions-fr"
                  />
                  <datalist id="asset-class-suggestions-fr">
                    {assetClasses.filter((ac) => ac.fr).map((ac) => (
                      <option key={ac.fr} value={ac.fr} />
                    ))}
                  </datalist>
                </div>
                
                {/* Sector */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.sectorEn')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterSectorEn')}
                      value={assetFormData.sector_en}
                      onChange={(e) => setAssetFormData({ ...assetFormData, sector_en: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.sectorDe')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterSectorDe')}
                      value={assetFormData.sector_de}
                      onChange={(e) => setAssetFormData({ ...assetFormData, sector_de: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                </div>

                {/* Sector FR */}
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">{t('assetLookup.sectorFr')}</Label>
                  <Input
                    placeholder={t('assetLookup.enterSectorFr')}
                    value={assetFormData.sector_fr}
                    onChange={(e) => setAssetFormData({ ...assetFormData, sector_fr: e.target.value })}
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                
                {/* Factor */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.factorEn')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterFactorEn')}
                      value={assetFormData.factor_en}
                      onChange={(e) => setAssetFormData({ ...assetFormData, factor_en: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.factorDe')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterFactorDe')}
                      value={assetFormData.factor_de}
                      onChange={(e) => setAssetFormData({ ...assetFormData, factor_de: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                </div>

                {/* Factor FR */}
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">{t('assetLookup.factorFr')}</Label>
                  <Input
                    placeholder={t('assetLookup.enterFactorFr')}
                    value={assetFormData.factor_fr}
                    onChange={(e) => setAssetFormData({ ...assetFormData, factor_fr: e.target.value })}
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                
                {/* Geography */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.geographyEn')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterGeographyEn')}
                      value={assetFormData.geography_en}
                      onChange={(e) => setAssetFormData({ ...assetFormData, geography_en: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">{t('assetLookup.geographyDe')}</Label>
                    <Input
                      placeholder={t('assetLookup.enterGeographyDe')}
                      value={assetFormData.geography_de}
                      onChange={(e) => setAssetFormData({ ...assetFormData, geography_de: e.target.value })}
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                </div>

                {/* Geography FR */}
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">{t('assetLookup.geographyFr')}</Label>
                  <Input
                    placeholder={t('assetLookup.enterGeographyFr')}
                    value={assetFormData.geography_fr}
                    onChange={(e) => setAssetFormData({ ...assetFormData, geography_fr: e.target.value })}
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => {
                    setAssetFormOpen(false);
                    setEditingAsset(null);
                  }} className="text-sm h-9">
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    type="submit"
                    disabled={assetSubmitting}
                    className="bg-[#0F766E] hover:bg-[#115E59] text-sm h-9"
                  >
                    {assetSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingAsset ? t('common.save') : t('common.create'))}
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="flex flex-col flex-1 overflow-hidden mt-3">
              {/* Tabs for Asset List vs Yahoo Import */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 overflow-hidden">
                <TabsList className="grid grid-cols-2 w-full mb-3">
                  <TabsTrigger value="list" className="text-xs sm:text-sm">{t('assetLookup.title')}</TabsTrigger>
                  <TabsTrigger value="yahoo" className="text-xs sm:text-sm">{t('assetLookup.yahooImport')}</TabsTrigger>
                </TabsList>

                {/* Asset List Tab */}
                <TabsContent value="list" className="flex flex-col flex-1 overflow-hidden mt-0">
                  {/* Search, Filter, Add Button */}
                  <div className="flex gap-2 mb-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder={t('assetLookup.searchPlaceholder')}
                        value={assetSearchQuery}
                        onChange={(e) => setAssetSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAssetSearch()}
                        className="pl-9 h-9 text-sm"
                      />
                    </div>
                    <Select value={assetClassFilter || "all"} onValueChange={handleAssetClassChange}>
                      <SelectTrigger className="w-[120px] sm:w-[160px] h-9 text-xs sm:text-sm">
                        <SelectValue placeholder={t('assetLookup.filterByAssetClass')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('assetLookup.allAssetClasses')}</SelectItem>
                        {assetClasses.map((ac) => (
                          <SelectItem key={ac.en} value={ac.en}>
                            {lookupLang === 'fr' ? (ac.fr || ac.en) : lookupLang === 'de' ? ac.de : ac.en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAssetSearch} variant="outline" className="h-9 px-3">
                      <Search className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => openAssetForm()} className="h-9 px-3 bg-[#0F766E] hover:bg-[#115E59]">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Asset List */}
                  <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg">
                    {assetLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-[#0F766E]" />
                      </div>
                    ) : assetLookups.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                        <Database className="w-10 h-10 mb-3 opacity-50" />
                        <p className="text-sm">{t('assetLookup.noAssets')}</p>
                        <p className="text-xs mt-1">{t('assetLookup.noAssetsText')}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {/* Header - Desktop */}
                        <div className="hidden lg:grid lg:grid-cols-12 gap-1 px-2 py-2 bg-slate-50 text-[10px] text-slate-500 uppercase tracking-wide sticky top-0">
                          <div className="col-span-1">{t('assetLookup.symbol')}</div>
                          <div className="col-span-2">{t('assetLookup.name')}</div>
                          <div className="col-span-2">{t('assetLookup.assetClass')}</div>
                          <div className="col-span-2">{t('assetLookup.sector')}</div>
                          <div className="col-span-2">{t('assetLookup.factor')}</div>
                          <div className="col-span-2">{t('assetLookup.geography')}</div>
                          <div className="col-span-1 text-right"></div>
                        </div>
                        {assetLookups.map((asset) => (
                          <div key={asset.id} className="px-2 py-2 hover:bg-slate-50">
                            {/* Desktop Layout */}
                            <div className="hidden lg:grid lg:grid-cols-12 gap-1 items-center">
                              <div className="col-span-1">
                                <span className="font-medium text-xs text-slate-900">{asset.symbol}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-xs text-slate-600 line-clamp-1">{asset.name}</span>
                              </div>
                              <div className="col-span-2">
                                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[9px]">
                                  {lookupLang === 'fr' ? (asset.asset_class_fr || asset.asset_class_en) : lookupLang === 'de' ? asset.asset_class_de : asset.asset_class_en}
                                </Badge>
                              </div>
                              <div className="col-span-2">
                                {(asset.sector_en || asset.sector_de) && (
                                  <Badge variant="outline" className="text-[9px]">
                                    {lookupLang === 'fr' ? (asset.sector_fr || asset.sector_en) : lookupLang === 'de' ? asset.sector_de : asset.sector_en}
                                  </Badge>
                                )}
                              </div>
                              <div className="col-span-2">
                                {(asset.factor_en || asset.factor_de) && (
                                  <Badge variant="outline" className="text-[9px]">
                                    {lookupLang === 'fr' ? (asset.factor_fr || asset.factor_en) : lookupLang === 'de' ? asset.factor_de : asset.factor_en}
                                  </Badge>
                                )}
                              </div>
                              <div className="col-span-2">
                                {(asset.geography_en || asset.geography_de) && (
                                  <Badge variant="outline" className="text-[9px]">
                                    {lookupLang === 'fr' ? (asset.geography_fr || asset.geography_en) : lookupLang === 'de' ? asset.geography_de : asset.geography_en}
                                  </Badge>
                                )}
                              </div>
                              <div className="col-span-1 flex justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openAssetForm(asset)} className="h-6 w-6 text-slate-400 hover:text-[#0F766E]">
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleAssetDelete(asset.id)} className="h-6 w-6 text-slate-400 hover:text-red-500">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Mobile Layout */}
                            <div className="lg:hidden space-y-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="font-medium text-sm text-slate-900">{asset.symbol}</span>
                                  <span className="text-xs text-slate-500 ml-2">{asset.currency}</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => openAssetForm(asset)} className="h-7 w-7 text-slate-400 hover:text-[#0F766E]">
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleAssetDelete(asset.id)} className="h-7 w-7 text-slate-400 hover:text-red-500">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                              <p className="text-xs text-slate-600 line-clamp-1">{asset.name}</p>
                              <div className="flex flex-wrap gap-1">
                                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]">
                                  {lookupLang === 'fr' ? (asset.asset_class_fr || asset.asset_class_en) : lookupLang === 'de' ? asset.asset_class_de : asset.asset_class_en}
                                </Badge>
                                {(asset.sector_en || asset.sector_de) && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {lookupLang === 'fr' ? (asset.sector_fr || asset.sector_en) : lookupLang === 'de' ? asset.sector_de : asset.sector_en}
                                  </Badge>
                                )}
                                {(asset.factor_en || asset.factor_de) && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {lookupLang === 'fr' ? (asset.factor_fr || asset.factor_en) : lookupLang === 'de' ? asset.factor_de : asset.factor_en}
                                  </Badge>
                                )}
                                {(asset.geography_en || asset.geography_de) && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {lookupLang === 'fr' ? (asset.geography_fr || asset.geography_en) : lookupLang === 'de' ? asset.geography_de : asset.geography_en}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Yahoo Import Tab */}
                <TabsContent value="yahoo" className="mt-0">
                  <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                    {/* Show search or classification form based on whether asset is selected */}
                    {!yahooSelectedAsset ? (
                      <>
                        {/* Search by Ticker */}
                        <div className="space-y-1.5">
                          <Label className="text-xs sm:text-sm">{t('portfolio.searchByTicker')}</Label>
                          <Input
                            data-testid="yahoo-search-symbol-input"
                            placeholder={t('portfolio.tickerPlaceholder')}
                            value={yahooSearchQuery}
                            onChange={(e) => setYahooSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleYahooSearch()}
                            className="h-10 sm:h-12 text-sm"
                          />
                        </div>
                        
                        {/* Search by Name */}
                        <div className="space-y-1.5">
                          <Label className="text-xs sm:text-sm">{t('portfolio.searchByName')}</Label>
                          <div className="flex gap-2">
                            <Input
                              data-testid="yahoo-search-name-input"
                              placeholder={t('portfolio.namePlaceholder')}
                              value={yahooNameSearchQuery}
                              onChange={(e) => setYahooNameSearchQuery(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleYahooSearch()}
                              className="h-10 sm:h-12 text-sm"
                            />
                            <Button 
                              onClick={handleYahooSearch} 
                              disabled={yahooSearching}
                              className="bg-[#0F766E] hover:bg-[#115E59] h-10 sm:h-12 px-4 sm:px-6"
                            >
                              {yahooSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* Search Results */}
                        {yahooSearchResults.length > 0 && (
                          <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                            {yahooSearchResults.map((result) => (
                              <button
                                key={result.symbol}
                                data-testid={`yahoo-search-result-${result.symbol}`}
                                onClick={() => setYahooSelectedAsset(result)}
                                className="w-full text-left p-2.5 sm:p-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                              >
                                <div className="flex justify-between items-center gap-2">
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium text-slate-900 text-sm">{result.symbol}</p>
                                    <p className="text-xs sm:text-sm text-slate-500 line-clamp-1">{result.name}</p>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <span className="text-[10px] sm:text-xs bg-slate-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">{result.type}</span>
                                    <p className="text-[10px] sm:text-xs text-slate-400 mt-1">{result.currency || result.region}</p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* No results message */}
                        {yahooSearchResults.length === 0 && (yahooSearchQuery || yahooNameSearchQuery) && !yahooSearching && (
                          <p className="text-sm text-slate-500 text-center py-4">{t('portfolio.noResults')}</p>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Selected Asset Display */}
                        <div className="bg-teal-50 p-3 sm:p-4 rounded-lg">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 text-sm">{yahooSelectedAsset.symbol}</p>
                              <p className="text-xs sm:text-sm text-slate-600 truncate">{yahooSelectedAsset.name}</p>
                              {yahooSelectedAsset.currency && (
                                <p className="text-xs text-slate-400 mt-0.5">{yahooSelectedAsset.currency}</p>
                              )}
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setYahooSelectedAsset(null)}
                              className="h-8 w-8"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Classification Form */}
                        <form onSubmit={handleYahooImport} className="space-y-3">
                          {/* Asset Class */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.assetClassEn')} *</Label>
                              <Input
                                placeholder={t('assetLookup.enterAssetClassEn')}
                                value={yahooAssetClassEn}
                                onChange={(e) => setYahooAssetClassEn(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                                list="yahoo-asset-class-suggestions-en"
                              />
                              <datalist id="yahoo-asset-class-suggestions-en">
                                {assetClasses.map((ac) => (
                                  <option key={ac.en} value={ac.en} />
                                ))}
                              </datalist>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.assetClassDe')} *</Label>
                              <Input
                                placeholder={t('assetLookup.enterAssetClassDe')}
                                value={yahooAssetClassDe}
                                onChange={(e) => setYahooAssetClassDe(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                                list="yahoo-asset-class-suggestions-de"
                              />
                              <datalist id="yahoo-asset-class-suggestions-de">
                                {assetClasses.map((ac) => (
                                  <option key={ac.de} value={ac.de} />
                                ))}
                              </datalist>
                            </div>
                          </div>

                          {/* Asset Class FR */}
                          <div className="space-y-1.5">
                            <Label className="text-xs sm:text-sm">{t('assetLookup.assetClassFr')}</Label>
                            <Input
                              placeholder={t('assetLookup.enterAssetClassFr')}
                              value={yahooAssetClassFr}
                              onChange={(e) => setYahooAssetClassFr(e.target.value)}
                              className="h-9 sm:h-10 text-sm"
                              list="yahoo-asset-class-suggestions-fr"
                            />
                            <datalist id="yahoo-asset-class-suggestions-fr">
                              {assetClasses.filter((ac) => ac.fr).map((ac) => (
                                <option key={ac.fr} value={ac.fr} />
                              ))}
                            </datalist>
                          </div>
                          
                          {/* Sector */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.sectorEn')}</Label>
                              <Input
                                placeholder={t('assetLookup.enterSectorEn')}
                                value={yahooSectorEn}
                                onChange={(e) => setYahooSectorEn(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.sectorDe')}</Label>
                              <Input
                                placeholder={t('assetLookup.enterSectorDe')}
                                value={yahooSectorDe}
                                onChange={(e) => setYahooSectorDe(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                              />
                            </div>
                          </div>

                          {/* Sector FR */}
                          <div className="space-y-1.5">
                            <Label className="text-xs sm:text-sm">{t('assetLookup.sectorFr')}</Label>
                            <Input
                              placeholder={t('assetLookup.enterSectorFr')}
                              value={yahooSectorFr}
                              onChange={(e) => setYahooSectorFr(e.target.value)}
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>
                          
                          {/* Factor */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.factorEn')}</Label>
                              <Input
                                placeholder={t('assetLookup.enterFactorEn')}
                                value={yahooFactorEn}
                                onChange={(e) => setYahooFactorEn(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.factorDe')}</Label>
                              <Input
                                placeholder={t('assetLookup.enterFactorDe')}
                                value={yahooFactorDe}
                                onChange={(e) => setYahooFactorDe(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                              />
                            </div>
                          </div>

                          {/* Factor FR */}
                          <div className="space-y-1.5">
                            <Label className="text-xs sm:text-sm">{t('assetLookup.factorFr')}</Label>
                            <Input
                              placeholder={t('assetLookup.enterFactorFr')}
                              value={yahooFactorFr}
                              onChange={(e) => setYahooFactorFr(e.target.value)}
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>
                          
                          {/* Geography */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.geographyEn')}</Label>
                              <Input
                                placeholder={t('assetLookup.enterGeographyEn')}
                                value={yahooGeographyEn}
                                onChange={(e) => setYahooGeographyEn(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs sm:text-sm">{t('assetLookup.geographyDe')}</Label>
                              <Input
                                placeholder={t('assetLookup.enterGeographyDe')}
                                value={yahooGeographyDe}
                                onChange={(e) => setYahooGeographyDe(e.target.value)}
                                className="h-9 sm:h-10 text-sm"
                              />
                            </div>
                          </div>

                          {/* Geography FR */}
                          <div className="space-y-1.5">
                            <Label className="text-xs sm:text-sm">{t('assetLookup.geographyFr')}</Label>
                            <Input
                              placeholder={t('assetLookup.enterGeographyFr')}
                              value={yahooGeographyFr}
                              onChange={(e) => setYahooGeographyFr(e.target.value)}
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>
                          
                          <div className="flex gap-2 pt-2">
                            <Button 
                              type="button"
                              variant="outline"
                              onClick={resetYahooImportForm}
                              className="flex-1 text-sm h-10"
                            >
                              {t('common.cancel')}
                            </Button>
                            <Button 
                              type="submit"
                              disabled={yahooImporting}
                              className="flex-1 bg-[#0F766E] hover:bg-[#115E59] text-sm h-10"
                            >
                              {yahooImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  {t('assetLookup.import')}
                                </>
                              )}
                            </Button>
                          </div>
                        </form>
                      </>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog open={analysisOpen} onOpenChange={(open) => {
        setAnalysisOpen(open);
        if (!open) {
          setAnalyzingPortfolio(null);
          setAnalysisResult(null);
        }
      }}>
        <DialogContent className="w-[95vw] max-w-4xl mx-auto max-h-[85vh] overflow-hidden flex flex-col p-3 sm:p-6" aria-describedby={undefined}>
          <DialogHeader className="pb-2">
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2 text-sm sm:text-lg flex-wrap">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
              <span>{t('analysis.title')}</span>
              {analyzingPortfolio && (
                <span className="text-slate-500 font-normal text-xs sm:text-base">- {analyzingPortfolio.name}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto mt-2 sm:mt-4">
            {analyzing ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16">
                <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 animate-spin text-purple-600 mb-4" />
                <p className="text-slate-500 text-sm">{t('analysis.analyzing')}</p>
                <p className="text-slate-400 text-xs mt-1">This may take 15-30 seconds...</p>
              </div>
            ) : analysisResult ? (
              <div className="space-y-4 sm:space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                  <div className="bg-slate-50 rounded-lg p-2 sm:p-3">
                    <p className="text-[10px] sm:text-xs text-slate-500">{t('analysis.totalValue')}</p>
                    <p className="text-xs sm:text-base font-semibold text-slate-900 truncate">
                      {analysisResult.summary.home_currency} {String(Math.round(analysisResult.summary.total_value || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, "'")}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2 sm:p-3">
                    <p className="text-[10px] sm:text-xs text-slate-500">{t('analysis.numAssets')}</p>
                    <p className="text-xs sm:text-base font-semibold text-slate-900">
                      {analysisResult.summary.num_assets}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-2 sm:p-3 col-span-2">
                    <p className="text-[10px] sm:text-xs text-purple-600">{t('analysis.generatedAt')}</p>
                    <p className="text-xs sm:text-sm text-purple-900">
                      {new Date(analysisResult.generated_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Allocation Breakdown */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {/* Asset Allocation */}
                  <div className="bg-blue-50 rounded-lg p-2 sm:p-4">
                    <h4 className="text-[10px] sm:text-xs font-semibold text-blue-700 mb-1 sm:mb-2">{t('analysis.assetAllocation')}</h4>
                    <div className="space-y-0.5 sm:space-y-1.5">
                      {Object.entries(analysisResult.summary.asset_allocation || {}).map(([type, pct]) => (
                        <div key={type} className="flex justify-between text-[10px] sm:text-xs">
                          <span className="text-blue-800 truncate mr-1">{type}</span>
                          <span className="font-medium text-blue-900 flex-shrink-0">{pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Currency Allocation */}
                  <div className="bg-green-50 rounded-lg p-2 sm:p-4">
                    <h4 className="text-[10px] sm:text-xs font-semibold text-green-700 mb-1 sm:mb-2">{t('analysis.currencyAllocation')}</h4>
                    <div className="space-y-0.5 sm:space-y-1.5">
                      {Object.entries(analysisResult.summary.currency_allocation || {}).map(([curr, pct]) => (
                        <div key={curr} className="flex justify-between text-[10px] sm:text-xs">
                          <span className="text-green-800">{curr}</span>
                          <span className="font-medium text-green-900">{pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Geography Allocation */}
                  <div className="bg-amber-50 rounded-lg p-2 sm:p-4">
                    <h4 className="text-[10px] sm:text-xs font-semibold text-amber-700 mb-1 sm:mb-2">{t('analysis.geographyAllocation')}</h4>
                    <div className="space-y-0.5 sm:space-y-1.5">
                      {Object.entries(analysisResult.summary.geography_allocation || {}).map(([geo, pct]) => (
                        <div key={geo} className="flex justify-between text-[10px] sm:text-xs">
                          <span className="text-amber-800 truncate mr-1">{geo}</span>
                          <span className="font-medium text-amber-900 flex-shrink-0">{pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* AI Analysis with Markdown */}
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-3 sm:p-6">
                  <h4 className="text-xs sm:text-sm font-semibold text-purple-800 mb-2 sm:mb-3 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t('analysis.aiInsights')}
                  </h4>
                  <div className="prose prose-sm prose-slate max-w-none text-slate-700 
                    prose-headings:text-purple-900 prose-headings:font-semibold 
                    prose-h3:text-xs prose-h3:sm:text-sm prose-h3:mt-3 prose-h3:mb-1.5
                    prose-p:text-[11px] prose-p:sm:text-sm prose-p:leading-relaxed prose-p:my-1.5
                    prose-strong:text-slate-800 prose-strong:font-semibold
                    prose-ul:text-[11px] prose-ul:sm:text-sm prose-ul:my-1 prose-ul:pl-4
                    prose-li:my-0.5
                    prose-ol:text-[11px] prose-ol:sm:text-sm prose-ol:my-1 prose-ol:pl-4">
                    <ReactMarkdown>
                      {analysisResult.analysis}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-slate-400">
                <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 mb-4 opacity-50" />
                <p className="text-xs sm:text-sm">Select a portfolio to analyze</p>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-3 sm:pt-4 border-t border-slate-100 mt-3 sm:mt-4">
            <Button variant="outline" onClick={() => setAnalysisOpen(false)} className="text-xs sm:text-sm h-8 sm:h-10">
              {t('analysis.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Risk Attitude Questionnaire Dialog */}
      <Dialog open={riskAttitudeOpen} onOpenChange={setRiskAttitudeOpen}>
        <DialogContent className="w-[95vw] max-w-2xl mx-auto max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="text-base sm:text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
              {t('riskProfile.part1Title')}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-slate-500">
              {t('riskProfile.questionnaireTitle')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Question 1 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q1')}</Label>
              <RadioGroup value={riskAttitudeAnswers.q1?.toString()} onValueChange={(v) => setRiskAttitudeAnswers({...riskAttitudeAnswers, q1: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q1a" />
                  <Label htmlFor="q1a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q1a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q1b" />
                  <Label htmlFor="q1b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q1b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q1c" />
                  <Label htmlFor="q1c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q1c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q1d" />
                  <Label htmlFor="q1d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q1d')}</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 2 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q2')}</Label>
              <RadioGroup value={riskAttitudeAnswers.q2?.toString()} onValueChange={(v) => setRiskAttitudeAnswers({...riskAttitudeAnswers, q2: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q2a" />
                  <Label htmlFor="q2a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q2a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q2b" />
                  <Label htmlFor="q2b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q2b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q2c" />
                  <Label htmlFor="q2c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q2c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q2d" />
                  <Label htmlFor="q2d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q2d')}</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 3 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q3')}</Label>
              <RadioGroup value={riskAttitudeAnswers.q3?.toString()} onValueChange={(v) => setRiskAttitudeAnswers({...riskAttitudeAnswers, q3: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q3a" />
                  <Label htmlFor="q3a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q3a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q3b" />
                  <Label htmlFor="q3b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q3b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q3c" />
                  <Label htmlFor="q3c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q3c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q3d" />
                  <Label htmlFor="q3d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q3d')}</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 4 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q4')}</Label>
              <RadioGroup value={riskAttitudeAnswers.q4?.toString()} onValueChange={(v) => setRiskAttitudeAnswers({...riskAttitudeAnswers, q4: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q4a" />
                  <Label htmlFor="q4a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q4a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q4b" />
                  <Label htmlFor="q4b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q4b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q4c" />
                  <Label htmlFor="q4c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q4c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q4d" />
                  <Label htmlFor="q4d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q4d')}</Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setRiskAttitudeOpen(false)}>
              {t('riskProfile.cancel')}
            </Button>
            <Button 
              onClick={handleSaveRiskAttitude}
              disabled={savingRiskProfile || !riskAttitudeAnswers.q1 || !riskAttitudeAnswers.q2 || !riskAttitudeAnswers.q3 || !riskAttitudeAnswers.q4}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingRiskProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('riskProfile.save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Risk Capacity Questionnaire Dialog */}
      <Dialog open={riskCapacityOpen} onOpenChange={setRiskCapacityOpen}>
        <DialogContent className="w-[95vw] max-w-2xl mx-auto max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="text-base sm:text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
              {t('riskProfile.part2Title')}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-slate-500">
              {t('riskProfile.questionnaireTitle')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Question 5 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q5')}</Label>
              <RadioGroup value={riskCapacityAnswers.q5?.toString()} onValueChange={(v) => setRiskCapacityAnswers({...riskCapacityAnswers, q5: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q5a" />
                  <Label htmlFor="q5a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q5a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q5b" />
                  <Label htmlFor="q5b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q5b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q5c" />
                  <Label htmlFor="q5c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q5c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q5d" />
                  <Label htmlFor="q5d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q5d')}</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 6 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q6')}</Label>
              <RadioGroup value={riskCapacityAnswers.q6?.toString()} onValueChange={(v) => setRiskCapacityAnswers({...riskCapacityAnswers, q6: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q6a" />
                  <Label htmlFor="q6a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q6a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q6b" />
                  <Label htmlFor="q6b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q6b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q6c" />
                  <Label htmlFor="q6c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q6c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q6d" />
                  <Label htmlFor="q6d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q6d')}</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 7 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q7')}</Label>
              <RadioGroup value={riskCapacityAnswers.q7?.toString()} onValueChange={(v) => setRiskCapacityAnswers({...riskCapacityAnswers, q7: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q7a" />
                  <Label htmlFor="q7a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q7a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q7b" />
                  <Label htmlFor="q7b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q7b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q7c" />
                  <Label htmlFor="q7c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q7c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q7d" />
                  <Label htmlFor="q7d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q7d')}</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 8 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('riskProfile.q8')}</Label>
              <RadioGroup value={riskCapacityAnswers.q8?.toString()} onValueChange={(v) => setRiskCapacityAnswers({...riskCapacityAnswers, q8: parseInt(v)})}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4" id="q8a" />
                  <Label htmlFor="q8a" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q8a')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3" id="q8b" />
                  <Label htmlFor="q8b" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q8b')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="2" id="q8c" />
                  <Label htmlFor="q8c" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q8c')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="1" id="q8d" />
                  <Label htmlFor="q8d" className="text-xs sm:text-sm font-normal cursor-pointer">{t('riskProfile.q8d')}</Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setRiskCapacityOpen(false)}>
              {t('riskProfile.cancel')}
            </Button>
            <Button 
              onClick={handleSaveRiskCapacity}
              disabled={savingRiskProfile || !riskCapacityAnswers.q5 || !riskCapacityAnswers.q6 || !riskCapacityAnswers.q7 || !riskCapacityAnswers.q8}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingRiskProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('riskProfile.save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
